import { DashboardSettings, IDashboardSettings, ISubview, IView, Subview, View, Widget } from "./Dashboard.types";
import { Layout, LayoutItem, ResponsiveLayouts } from "react-grid-layout";
import { CardMetadataLookup, WidgetStoreEntity, WidgetsToAdd, getWidgetsWithLayout } from "./UserDashboardSettingsUtility";
import { cast, clone, getSnapshot, SnapshotIn, types } from "mobx-state-tree";
import { newGuid, zu } from "Shared/Utils";

import { ShareStatusEnum } from "Enumerations";

export const UserDashboardSettingsStore = types
    .model("userDashboardSetting", {
        userSetting: types.optional(DashboardSettings, {})
    })
    .volatile(() => ({
        volatilePerspectiveUserSetting: DashboardSettings.create({}),
        volatilePerspectiveUserAlias: "",
        volatilePerspectiveUserName: ""
    }))
    .views((self) => ({
        get userSettingData() {
            return !!self.volatilePerspectiveUserAlias ? self.volatilePerspectiveUserSetting : self.userSetting;
        },
        getViewData(viewId: string) {
            return (!!self.volatilePerspectiveUserAlias ? self.volatilePerspectiveUserSetting : self.userSetting).views.find((x) => x.key === viewId);
        },
        get perspectiveUserAlias() {
            return self.volatilePerspectiveUserAlias;
        },
        get perspectiveUserName() {
            return self.volatilePerspectiveUserName;
        }
    }))
    .actions((self) => ({
        saveView(viewKey: string, viewTitle: string, subviewId?: string, widgets?: WidgetsToAdd) {
            const view = self.userSettingData.views.find((i) => i.key === viewKey);
            const newSubviewId = newGuid();
            if (view) {
                view.title = viewTitle;
                view.path = `/${viewKey}`;
                const subview = subviewId ? view.subviews?.find((i) => i.key === subviewId) : undefined;
                if (!subview || !subviewId) {
                    const newSubview = Subview.create({ key: newSubviewId, title: viewTitle, path: `/${newSubviewId}` });
                    view.subviews?.push(newSubview);
                }
                if (widgets) {
                    this.addWidgets(view.key, subviewId ?? newSubviewId, widgets.cards, widgets.getCardMetadata);
                }
            } else {
                const newSubview = Subview.create({
                    key: subviewId ? subviewId : newSubviewId,
                    title: viewTitle,
                    path: `/${subviewId ? subviewId : newSubviewId}`,
                    cards: widgets ? getWidgetsWithLayout(widgets.cards, widgets.getCardMetadata).map((widget) => Widget.create(widget)) : []
                });
                const newView = View.create({
                    key: viewKey,
                    title: viewTitle,
                    path: `/${viewKey}`,
                    subviews: [newSubview]
                });
                self.userSettingData.views.push(newView);
            }
        },
        shareView(viewKey: string, sharedKey: string, creator: string) {
            const existedViews = self.userSettingData.views.slice();
            const view = existedViews.find((i) => i.key === viewKey);
            if (view) {
                view.sharedkey = sharedKey;
                view.shareStatus = ShareStatusEnum.Shared;
                view.sharedOwner = creator;
                this.updateUserSettingDataViews(existedViews);
            }
        },
        publishView(viewKey: string, sharedKey: string, creator: string, editors?: string[]) {
            const existedViews = self.userSettingData.views.slice();
            const view = existedViews.find((i) => i.key === viewKey);
            if (view) {
                view.sharedkey = sharedKey;
                view.shareStatus = ShareStatusEnum.Published;
                view.sharedOwner = creator;
                if (editors) {
                    view.editors?.replace(editors);
                } else {
                    view.editors?.clear();
                }
                this.updateUserSettingDataViews(existedViews);
            }
        },
        unshareView(viewKey: string) {
            const existedViews = self.userSettingData.views.slice();
            const view = self.userSettingData.views.find((i) => i.key === viewKey);
            if (view) {
                view.sharedkey = undefined;
                view.shareStatus = ShareStatusEnum.Unshared;
                view.editors?.clear();
                this.updateUserSettingDataViews(existedViews);
            }
        },
        deleteView(viewKey: string) {
            const indx = self.userSettingData.views.findIndex((i) => i.key === viewKey);
            if (indx >= 0) {
                self.userSettingData.views.splice(indx, 1);
            }
        },
        addSubView(viewKey: string, subviewId: string, name: string) {
            if (self.volatilePerspectiveUserAlias) {
                return;
            }
            self.userSetting.views.forEach((view) => {
                if (view.key === viewKey) {
                    if (!view.subviews) {
                        view.subviews = cast([]);
                    }
                    if (Array.isArray(view.subviews)) {
                        view.subviews.push(Subview.create({ key: subviewId, title: name, path: `/${subviewId}` }));
                    }
                }
            });
        },
        deleteSubview(viewKey: string, subviewId: string) {
            const view = self.userSettingData.views.find((v) => v.key === viewKey);
            if (!view || !view.subviews) {
                return;
            }
            const subviewIndex = view.subviews.findIndex((sub) => sub.key === subviewId);
            if (subviewIndex === -1) {
                return;
            }
            view.subviews.splice(subviewIndex, 1);
            view.subviews = clone(view.subviews);
        },
        updateSubviews(viewKey: string, subviews: { id: string; name: string }[]) {
            const view = self.userSettingData.views.find((v) => v.key === viewKey);
            if (!view || !view.subviews) {
                return;
            }

            const existingMap = new Map(view.subviews.map((sv) => [sv.key, getSnapshot(sv)]));
            const reorderedSnapshots = subviews
                .map((incoming) => {
                    const snapshot = existingMap.get(incoming.id);
                    return snapshot ? { ...snapshot, title: incoming.name } : undefined;
                })
                .filter((snap) => snap !== undefined);
            view.subviews.clear();
            reorderedSnapshots.forEach((snap) => {
                view.subviews?.push(Subview.create(snap));
            });
        },
        updateViews(views: { id: string; name: string }[]) {
            const existedViews = self.userSettingData.views.slice();
            for (let i = 0; i < views.length; i++) {
                const newView = views[i];
                const existedView = existedViews[i];
                if (newView) {
                    if (newView.id === existedView?.key) {
                        if (newView.name !== existedView.title) {
                            existedView.title = newView.name;
                        }
                    } else {
                        const index = existedViews.findIndex((v) => v.key === newView.id);
                        const sameView = existedViews[index];
                        if (index >= 0 && sameView) {
                            sameView.title = newView.name;
                            existedViews.splice(i, 1, sameView);
                            if (existedView) {
                                existedViews.splice(index, 1, existedView);
                            }
                        }
                    }
                }
            }
            if (views.length < existedViews.length) {
                existedViews.splice(views.length);
            }
            this.updateUserSettingDataViews(existedViews);
        },
        addWidgets(viewKey: string, subviewId: string, cards: WidgetStoreEntity[], getCardMetadata: CardMetadataLookup) {
            const view = self.userSettingData.views.find((i) => i.key === viewKey);
            if (!view) {
                return;
            }
            const subview = view.subviews?.find((i) => i.key === subviewId);
            if (!subview) {
                return;
            }
            let pointYSm = 0;
            let pointYLg = 0;
            let pointXLg = 0;
            subview.cards.forEach((card) => {
                card.layouts.forEach((layout) => {
                    if (layout.breakpointName === "sm") {
                        pointYSm = Math.max(pointYSm, layout.y + layout.h);
                    } else if (layout.breakpointName === "lg" && (layout.y > pointYLg || (layout.y === pointYLg && layout.x + layout.w > pointXLg))) {
                        pointXLg = layout.x + layout.w;
                        pointYLg = layout.y;
                    }
                });
            });
            const widgets = getWidgetsWithLayout(cards, getCardMetadata, pointYSm, pointYLg, pointXLg);
            widgets.forEach((widget) => {
                subview.cards.push(Widget.create(widget));
            });
        },
        removeWidget(viewWidgetKey: string) {
            let isWidgetRemoved = false;
            for (const view of self.userSettingData.views) {
                if (!view.subviews) {
                    continue;
                }
                for (const subview of view.subviews) {
                    const indx = subview.cards.findIndex((w) => w.viewWidgetKey === viewWidgetKey);
                    if (indx >= 0) {
                        subview.cards.splice(indx, 1);
                        isWidgetRemoved = true;
                        break;
                    }
                }
                if (isWidgetRemoved) {
                    break;
                }
            }
        },
        updateWidgetConfig(viewWidgetKey: string, configData?: string) {
            let isWidgetUpdated = false;
            for (const view of self.userSettingData.views) {
                if (!view.subviews) {
                    continue;
                }
                for (const subview of view.subviews) {
                    const widget = subview.cards.find((w) => w.viewWidgetKey === viewWidgetKey);
                    if (widget) {
                        widget.config = configData;
                        isWidgetUpdated = true;
                        break;
                    }
                }
                if (isWidgetUpdated) {
                    break;
                }
            }
        },
        updateWidgetConfigFields(viewWidgetKey: string, configData: object) {
            let isWidgetUpdated = false;
            for (const view of self.userSettingData.views) {
                if (!view.subviews) {
                    continue;
                }
                for (const subview of view.subviews) {
                    const widget = subview.cards.find((w) => w.viewWidgetKey === viewWidgetKey);
                    if (widget) {
                        const config = zu.safeJsonParseObject(widget.config ?? "{}");
                        widget.config = JSON.stringify({
                            ...config,
                            ...configData
                        });
                        isWidgetUpdated = true;
                        break;
                    }
                }
                if (isWidgetUpdated) {
                    break;
                }
            }
        },
        saveLayouts(viewKey: string, subviewId: string, allLayouts: ResponsiveLayouts) {
            if (self.volatilePerspectiveUserAlias) {
                return;
            }
            const view = self.userSettingData.views.find((i) => i.key === viewKey);
            if (view) {
                const subview = view.subviews?.find((i) => i.key === subviewId);
                if (subview) {
                    Object.keys(allLayouts).forEach((breakpointName: string) => {
                        const layouts: Layout | undefined = allLayouts[breakpointName];
                        if (layouts) {
                            layouts.forEach((l: LayoutItem) => {
                                const layoutToUpdate = subview.cards
                                    .find((w) => w.viewWidgetKey === l.i)
                                    ?.layouts.find((o) => o.breakpointName === breakpointName);
                                if (layoutToUpdate) {
                                    layoutToUpdate.h = l.h;
                                    layoutToUpdate.x = l.x;
                                    layoutToUpdate.y = l.y;
                                    layoutToUpdate.w = l.w;
                                }
                            });
                        }
                    });
                }
            }
        },
        saveSharedView(
            viewKey: string,
            viewTitle: string,
            sharedOwner: string,
            sharedKey: string,
            shareStatus: ShareStatusEnum,
            sharedEditors?: string[],
            subviews?: ISubview[] | SnapshotIn<typeof Subview>[],
            temporal?: boolean
        ) {
            if (self.volatilePerspectiveUserAlias) {
                return;
            }
            const view = self.userSettingData.views.find((i) => i.key === viewKey);
            if (view) {
                view.title = viewTitle;
                view.path = `/${viewKey}`;
                view.sharedOwner = sharedOwner;
                view.sharedkey = sharedKey;
                view.shareStatus = shareStatus;
                if (sharedEditors) {
                    view.editors?.replace(sharedEditors);
                } else {
                    view.editors?.clear();
                }
                // Reconcile by key instead of clearing and re-adding, so unchanged subviews, cards and layouts stay the same objects
                // (components hold on to them) and syncing an unchanged published view doesn't change the settings.
                if (view.subviews) {
                    view.subviews = cast(subviews ?? []);
                }
            } else {
                const newView = View.create({
                    key: viewKey,
                    title: viewTitle,
                    path: `/${viewKey}`,
                    sharedOwner: sharedOwner,
                    sharedkey: sharedKey,
                    shareStatus: shareStatus,
                    editors: sharedEditors,
                    subviews: subviews,
                    temporal
                });
                self.userSettingData.views.push(newView);
            }
        },
        setPerspectiveUser(userAlias: string, userName: string) {
            self.volatilePerspectiveUserAlias = userAlias;
            self.volatilePerspectiveUserName = userName;
        },
        setPerspectiveUserDashboardSetting(dashboardSetting: IDashboardSettings) {
            self.volatilePerspectiveUserSetting = DashboardSettings.create(dashboardSetting);
        },
        updateUserSettingDataViews(views: IView[]) {
            self.userSettingData.views = cast(views);
            self.userSettingData.views = clone(self.userSettingData.views);
        },
        updateLegacyDashboardSettingFormat() {
            self.userSetting.views.forEach((view) => {
                if (view.cards && (!view.subviews || view.subviews.length === 0)) {
                    if (!view.subviews) {
                        view.subviews = cast([]);
                    }
                    const subViewId = newGuid();
                    const subView = Subview.create({
                        key: subViewId,
                        title: view.title,
                        path: `/${subViewId}`,
                        cards: view.cards.map((card) => Widget.create(clone(card)))
                    });
                    view.subviews?.push(subView);
                    view.cards = undefined;
                }
            });
        },
        setDisplayViewGalleryPopover(value: boolean) {
            self.userSetting.displayViewGalleryPopover = value;
        },
        setDisplayRedirectionBanner(value: boolean) {
            self.userSetting.displayRedirectionBanner = value;
        },
        setDisplayDashboardTour(value: boolean) {
            self.userSetting.displayDashboardTour = value;
        },
        setDisplayNewUserDashboardTour(value: boolean) {
            self.userSetting.displayNewUserDashboardTour = value;
        }
    }));
